import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { useTranslation } from "react-i18next";
import { QRCodeSVG } from "qrcode.react";
import { baseUrl } from "../../api";
import { isWebUsbSupported } from "../../features/deviceInstaller/utils/browserCompat";
import {
  getConnectedDevice,
  getInstalledPackages,
} from "../../features/deviceInstaller/state/deviceInstallerSlice";
import { getBuildInstallStatus } from "../../features/deviceInstaller/utils/buildStatus";
import {
  detectDeviceFamily,
  isApkPlatform,
  isQuestPlatform,
  isWebGLPlatform,
} from "../../features/deviceInstaller/utils/platformCompat";
import { formatSize } from "../../common/utils/formatSize";
import { installApkFromUrl } from "../../features/deviceInstaller/utils/adbInstaller";
import { launchApp } from "../../features/deviceInstaller/utils/appLauncher";
import { useLaunchApp } from "../../features/deviceInstaller/hooks/useLaunchApp";
import DeviceConnectionBar from "../../features/deviceInstaller/components/DeviceConnectionBar";
import InstallProgressModal from "../../features/deviceInstaller/components/InstallProgressModal";
import UninstallButton from "../../features/deviceInstaller/components/UninstallButton";
import {
  CheckIcon,
  DownloadArrowIcon,
  PlayIcon,
  Spinner,
  WarningCircleIcon,
  DevicePlainIcon,
} from "../../features/deviceInstaller/components/icons";

interface BuildInfo {
  buildId: number;
  name: string | null;
  targetPlatform: string;
  version: number;
  sizeInBytes: number | null;
  packageName: string | null;
  downloadUrl: string;
  publicUrl: string | null;
  linkType: "download" | "app";
  description: string;
  projectGroupName: string | null;
  projectGroupThumbnailUrl: string | null;
  organizationName: string | null;
  organizationLogoUrl: string | null;
  expiresAt: number | null;
}

type PageState = "loading" | "success" | "error";

/* ─── Device detection ─── */

type DeviceType = "quest" | "android" | "ios" | "desktop";

function detectDevice(): DeviceType {
  const ua = navigator.userAgent;
  if (/OculusBrowser/i.test(ua)) return "quest";
  if (/Android/i.test(ua)) return "android";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  return "desktop";
}

/* ─── Platform helpers ─── */

const PLATFORM_LABELS: Record<string, string> = {
  Android: "Android",
  OculusQuest: "Meta Quest",
  MetaQuest: "Meta Quest",
  PicoNeo: "Pico",
  Windows10: "Windows",
  WebGLApp: "WebGL",
  "WebGL App": "WebGL",
};

/* ─── Contextual guidance ─── */

interface ContextGuidance {
  type: "success" | "warning" | "info";
  icon: "check" | "warning" | "info" | "qr";
  messageKey: string;
  showQR: boolean;
  instructionKeys: string[] | null;
  ctaLabelKey: string;
  ctaEnabled: boolean;
}

function getContextGuidance(
  platform: string,
  device: DeviceType,
  linkType: "download" | "app",
  hasPublicUrl: boolean,
): ContextGuidance {
  // WebGL app link — universal, zero friction
  if (isWebGLPlatform(platform) && linkType === "app" && hasPublicUrl) {
    return {
      type: "success",
      icon: "check",
      messageKey: "publicDownload.webgl.appReady",
      showQR: false,
      instructionKeys: null,
      ctaLabelKey: "publicDownload.openApp",
      ctaEnabled: true,
    };
  }

  // WebGL download
  if (isWebGLPlatform(platform)) {
    return {
      type: "info",
      icon: "info",
      messageKey: "publicDownload.webgl.downloadArchive",
      showQR: false,
      instructionKeys: [
        "publicDownload.webgl.step1",
        "publicDownload.webgl.step2",
        "publicDownload.webgl.step3",
      ],
      ctaLabelKey: "publicDownload.download",
      ctaEnabled: true,
    };
  }

  // Quest build
  if (isQuestPlatform(platform)) {
    if (device === "quest") {
      return {
        type: "success",
        icon: "check",
        messageKey: "publicDownload.quest.onDevice",
        showQR: false,
        instructionKeys: [
          "publicDownload.quest.step1",
          "publicDownload.quest.step2",
          "publicDownload.quest.step3",
        ],
        ctaLabelKey: "publicDownload.downloadApp",
        ctaEnabled: true,
      };
    }
    if (device === "desktop") {
      return {
        type: "info",
        icon: "info",
        messageKey: isWebUsbSupported()
          ? "publicDownload.quest.onDesktopUsb"
          : "publicDownload.quest.onDesktop",
        showQR: false,
        instructionKeys: isWebUsbSupported()
          ? null
          : [
              "publicDownload.quest.adbStep1",
              "publicDownload.quest.adbStep2",
              "publicDownload.quest.adbStep3",
              "publicDownload.quest.adbStep4",
            ],
        ctaLabelKey: "publicDownload.downloadApk",
        ctaEnabled: true,
      };
    }
    return {
      type: "warning",
      icon: "warning",
      messageKey: "publicDownload.quest.onMobile",
      showQR: false,
      instructionKeys: null,
      ctaLabelKey: "publicDownload.downloadApk",
      ctaEnabled: true,
    };
  }

  // Pico build
  if (platform === "PicoNeo") {
    if (device === "desktop") {
      return {
        type: "info",
        icon: "info",
        messageKey: isWebUsbSupported()
          ? "publicDownload.pico.onDesktopUsb"
          : "publicDownload.pico.onDesktop",
        showQR: false,
        instructionKeys: isWebUsbSupported()
          ? null
          : [
              "publicDownload.pico.adbStep1",
              "publicDownload.pico.adbStep2",
              "publicDownload.pico.adbStep3",
              "publicDownload.pico.adbStep4",
            ],
        ctaLabelKey: "publicDownload.downloadApk",
        ctaEnabled: true,
      };
    }
    return {
      type: "warning",
      icon: "warning",
      messageKey: "publicDownload.pico.onMobile",
      showQR: false,
      instructionKeys: null,
      ctaLabelKey: "publicDownload.downloadApk",
      ctaEnabled: true,
    };
  }

  // Android build
  if (platform === "Android") {
    if (device === "android") {
      return {
        type: "success",
        icon: "check",
        messageKey: "publicDownload.android.onDevice",
        showQR: false,
        instructionKeys: [
          "publicDownload.android.step1",
          "publicDownload.android.step2",
          "publicDownload.android.step3",
        ],
        ctaLabelKey: "publicDownload.downloadApp",
        ctaEnabled: true,
      };
    }
    if (device === "desktop") {
      return {
        type: "info",
        icon: isWebUsbSupported() ? "info" : "qr",
        messageKey: isWebUsbSupported()
          ? "publicDownload.android.onDesktopUsb"
          : "publicDownload.android.onDesktop",
        showQR: true,
        instructionKeys: null,
        ctaLabelKey: "publicDownload.downloadAnyway",
        ctaEnabled: true,
      };
    }
    if (device === "ios") {
      return {
        type: "warning",
        icon: "warning",
        messageKey: "publicDownload.android.onIos",
        showQR: true,
        instructionKeys: null,
        ctaLabelKey: "publicDownload.downloadAnyway",
        ctaEnabled: true,
      };
    }
    return {
      type: "success",
      icon: "check",
      messageKey: "publicDownload.android.onQuest",
      showQR: false,
      instructionKeys: ["publicDownload.android.questStep1", "publicDownload.android.questStep2"],
      ctaLabelKey: "publicDownload.downloadApp",
      ctaEnabled: true,
    };
  }

  // Windows build
  if (platform === "Windows10") {
    if (device === "desktop") {
      return {
        type: "success",
        icon: "check",
        messageKey: "publicDownload.windows.onDesktop",
        showQR: false,
        instructionKeys: [
          "publicDownload.windows.step1",
          "publicDownload.windows.step2",
          "publicDownload.windows.step3",
        ],
        ctaLabelKey: "publicDownload.download",
        ctaEnabled: true,
      };
    }
    return {
      type: "warning",
      icon: "warning",
      messageKey: "publicDownload.windows.onMobile",
      showQR: false,
      instructionKeys: null,
      ctaLabelKey: "publicDownload.downloadAnyway",
      ctaEnabled: true,
    };
  }

  // Fallback
  return {
    type: "info",
    icon: "info",
    messageKey: "publicDownload.fallback",
    showQR: false,
    instructionKeys: null,
    ctaLabelKey: "publicDownload.download",
    ctaEnabled: true,
  };
}

/* ─── Time remaining ─── */

function formatTimeRemaining(
  expiresAt: number,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const now = Date.now();
  const diff = expiresAt - now;
  if (diff <= 0) return t("publicDownload.expired");
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours < 24) return t("publicDownload.expiresInHours", { hours });
  const days = Math.floor(hours / 24);
  if (days === 1) return t("publicDownload.expiresTomorrow");
  if (days < 30) return t("publicDownload.expiresInDays", { days });
  const months = Math.floor(days / 30);
  if (months === 1) return t("publicDownload.expiresInOneMonth");
  return t("publicDownload.expiresInMonths", { months });
}

/* ─── Icons ─── */

const CheckCircleIcon = () => (
  <svg
    className="w-4 h-4 text-green-500 shrink-0 mt-0.5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const WarningIcon = () => (
  <svg
    className="w-4 h-4 text-amber-500 shrink-0 mt-0.5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z"
    />
  </svg>
);

const InfoIcon = () => (
  <svg
    className="w-4 h-4 text-blue-500 shrink-0 mt-0.5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
    />
  </svg>
);

const QRSmallIcon = () => (
  <svg
    className="w-4 h-4 text-blue-500 shrink-0 mt-0.5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6.75 6.75h.75v.75h-.75v-.75zM6.75 16.5h.75v.75h-.75v-.75zM16.5 6.75h.75v.75H16.5v-.75zM13.5 13.5h.75v.75h-.75v-.75zM13.5 19.5h.75v.75h-.75v-.75zM19.5 13.5h.75v.75h-.75v-.75zM19.5 19.5h.75v.75h-.75v-.75zM16.5 16.5h.75v.75H16.5v-.75z"
    />
  </svg>
);

function GuidanceIcon({ icon }: { icon: ContextGuidance["icon"] }) {
  switch (icon) {
    case "check":
      return <CheckCircleIcon />;
    case "warning":
      return <WarningIcon />;
    case "qr":
      return <QRSmallIcon />;
    default:
      return <InfoIcon />;
  }
}

const GUIDANCE_STYLES: Record<
  ContextGuidance["type"],
  { bg: string; border: string; text: string }
> = {
  success: { bg: "bg-green-50", border: "border-green-100", text: "text-green-700" },
  warning: { bg: "bg-amber-50", border: "border-amber-100", text: "text-amber-700" },
  info: { bg: "bg-slate-50", border: "border-slate-200", text: "text-blue-700" },
};

/* ─── WebUSB Install Section ─── */

interface WebUsbInstallSectionProps {
  build: BuildInfo;
  downloadUrl: string;
}

const WebUsbInstallSection = ({ build, downloadUrl }: WebUsbInstallSectionProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const device = useAppSelector(getConnectedDevice);
  const installedPackages = useAppSelector(getInstalledPackages);
  const [showModal, setShowModal] = useState(false);

  const isDeviceReady = device?.status === "ready";
  const deviceFamily = device ? detectDeviceFamily(device.productName) : null;

  const buildStatus = getBuildInstallStatus({
    isDeviceReady: !!isDeviceReady,
    deviceFamily,
    targetPlatform: build.targetPlatform,
    packageName: build.packageName,
    buildVersion: build.version,
    installedPackages,
  });

  const deviceVersionCode = build.packageName
    ? installedPackages[build.packageName]?.versionCode
    : null;

  const handleInstall = async () => {
    if (!isDeviceReady) return;
    setShowModal(true);
    const fileName = build.name || `wixar_build_${build.buildId}.apk`;
    await installApkFromUrl(
      downloadUrl,
      fileName,
      build.buildId,
      dispatch,
      build.packageName || undefined,
    );
  };

  const onLaunch = useCallback(async () => {
    if (!build.packageName) throw new Error("No package name");
    await launchApp(build.packageName);
  }, [build.packageName]);
  const { status: launchStatus, launch: handleLaunch } = useLaunchApp(onLaunch);

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mt-4">
      {/* Header */}
      <div className="px-5 pt-4 pb-3 border-b border-gray-100">
        <h2 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
          <UsbIcon />
          {t("publicDownload.usbInstallTitle")}
        </h2>
        <DeviceConnectionBar />
      </div>

      {/* Device status & actions */}
      <div className="px-5 py-4">
        {!device && (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <DevicePlainIcon className="w-10 h-10 text-gray-300" />
            <p className="text-xs text-gray-400">{t("publicDownload.connectDevicePrompt")}</p>
          </div>
        )}

        {isDeviceReady && buildStatus === "incompatible" && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-100">
            <WarningCircleIcon className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">
              {t("publicDownload.incompatibleDevice", { device: device.productName })}
            </p>
          </div>
        )}

        {isDeviceReady && buildStatus === "install" && (
          <div className="flex flex-col gap-3">
            <div className="flex items-start gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <InfoIcon />
              <p className="text-xs text-blue-700">
                {t("publicDownload.notInstalled", { device: device.productName })}
              </p>
            </div>
            <button
              onClick={handleInstall}
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg bg-green-500 hover:bg-green-600 active:bg-green-700 text-white text-sm font-medium transition-colors"
            >
              <DownloadArrowIcon className="w-4 h-4" />
              {t("publicDownload.installOn", { device: device.productName })}
            </button>
          </div>
        )}

        {isDeviceReady && buildStatus === "outdated" && (
          <div className="flex flex-col gap-3">
            <div className="flex items-start gap-2 p-3 bg-orange-50 rounded-lg border border-orange-200">
              <WarningCircleIcon className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
              <p className="text-xs text-orange-700">
                {t("publicDownload.outdatedVersion", {
                  installed: deviceVersionCode,
                  available: build.version,
                })}
              </p>
            </div>
            <button
              onClick={handleInstall}
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg bg-green-500 hover:bg-green-600 active:bg-green-700 text-white text-sm font-medium transition-colors"
            >
              <DownloadArrowIcon className="w-4 h-4" />
              {t("publicDownload.updateTo", { version: build.version })}
            </button>
          </div>
        )}

        {isDeviceReady && buildStatus === "installed" && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-100">
              <CheckIcon className="w-4 h-4 text-green-500" />
              <p className="text-xs text-green-700 font-medium">
                {t("publicDownload.installedVersion", {
                  version: build.version,
                  device: device.productName,
                })}
              </p>
            </div>
            {build.packageName && (
              <div className="flex gap-2">
                <button
                  onClick={handleLaunch}
                  disabled={launchStatus === "launching"}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    launchStatus === "launched"
                      ? "bg-green-50 text-green-600 border border-green-200"
                      : launchStatus === "error"
                      ? "bg-red-50 text-red-500 border border-red-200 hover:bg-red-100"
                      : "bg-green-50 text-green-700 hover:bg-green-100 border border-green-200"
                  }`}
                >
                  {launchStatus === "launching" ? (
                    <Spinner className="w-4 h-4" color="green" />
                  ) : launchStatus === "launched" ? (
                    <CheckIcon className="w-4 h-4" />
                  ) : (
                    <PlayIcon className="w-4 h-4" />
                  )}
                  {launchStatus === "launched"
                    ? t("publicDownload.launched")
                    : launchStatus === "error"
                    ? t("publicDownload.retryLaunch")
                    : t("publicDownload.launch")}
                </button>
                <UninstallButton
                  packageName={build.packageName}
                  confirmBeforeUninstall
                  className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-400 hover:text-red-500 border border-gray-200 hover:border-red-200 hover:bg-red-50 transition-colors"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {showModal && <InstallProgressModal onClose={() => setShowModal(false)} />}
    </div>
  );
};

/* ─── USB Icon ─── */

const UsbIcon = () => (
  <svg className="w-4 h-4 text-gray-500" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15 7.5V2H9v5.5l3 3 3-3zM12 11.83L9.91 9.74 9 10.65V14c-1.16.41-2 1.51-2 2.81 0 1.66 1.34 3 3 3V22h4v-2.19c1.66 0 3-1.34 3-3 0-1.3-.84-2.4-2-2.81V10.65l-.91-.91L12 11.83zM11 4h2v2h-2V4z" />
  </svg>
);

/* ─── Component ─── */

const PublicDownloadPage = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<PageState>("loading");
  const [build, setBuild] = useState<BuildInfo | null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [hasClicked, setHasClicked] = useState(false);
  const [device] = useState<DeviceType>(() => detectDevice());

  useEffect(() => {
    if (!token) {
      setState("error");
      setErrorMsg(t("publicDownload.invalidLink"));
      return;
    }

    fetch(`${baseUrl}/api/builds/public-download/${token}`)
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || t("publicDownload.invalidOrExpired"));
        }
        return res.json();
      })
      .then((data: BuildInfo) => {
        setBuild(data);
        setState("success");
      })
      .catch((err) => {
        setErrorMsg(err.message || t("publicDownload.invalidOrExpired"));
        setState("error");
      });
  }, [token, t]);

  const pageUrl = window.location.href;
  const fileEndpoint = `${baseUrl}/api/builds/public-file/${token}`;
  const effectiveDownloadUrl =
    build && isApkPlatform(build.targetPlatform) ? fileEndpoint : build?.downloadUrl || "";
  const platformLabel = build ? PLATFORM_LABELS[build.targetPlatform] || build.targetPlatform : "";

  const guidance = build
    ? getContextGuidance(build.targetPlatform, device, build.linkType, !!build.publicUrl)
    : null;
  const guidanceStyle = guidance ? GUIDANCE_STYLES[guidance.type] : null;

  const isWebGLApp =
    build && isWebGLPlatform(build.targetPlatform) && build.linkType === "app" && build.publicUrl;

  // Show WebUSB install section on desktop for Android-based builds when browser supports it
  const showWebUsb =
    build && isApkPlatform(build.targetPlatform) && device === "desktop" && isWebUsbSupported();

  return (
    <div
      className="min-h-screen bg-gray-50"
      style={{ fontFamily: "Roboto, system-ui, -apple-system, sans-serif" }}
    >
      <div className="max-w-lg mx-auto px-5 pt-6 pb-8">
        {/* Org logo */}
        {state === "success" && build && (
          <div className="mb-6 flex items-center gap-3">
            {build.organizationLogoUrl ? (
              <img
                src={build.organizationLogoUrl}
                alt={build.organizationName || ""}
                className="h-8 w-auto object-contain"
              />
            ) : build.organizationName ? (
              <span className="text-sm font-medium text-gray-700">{build.organizationName}</span>
            ) : null}
          </div>
        )}

        {/* Loading */}
        {state === "loading" && (
          <div className="flex flex-col items-center gap-3 py-16">
            <div className="w-6 h-6 rounded-full border-2 border-green-500 border-t-transparent animate-spin" />
            <p className="text-sm text-gray-400">{t("publicDownload.loading")}</p>
          </div>
        )}

        {/* Error */}
        {state === "error" && (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center mt-8">
            <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-red-50 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z"
                />
              </svg>
            </div>
            <p className="text-gray-800 font-medium text-sm">{errorMsg}</p>
            <p className="text-gray-400 text-xs mt-1">{t("publicDownload.linkMayExpired")}</p>
          </div>
        )}

        {/* Success */}
        {state === "success" && build && guidance && guidanceStyle && (
          <>
            {/* Main card */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {/* App header */}
              <div className="p-5 pb-4">
                <div className="flex items-center gap-3.5">
                  {build.projectGroupThumbnailUrl ? (
                    <img
                      src={build.projectGroupThumbnailUrl}
                      alt=""
                      className="w-12 h-12 rounded-lg object-cover shrink-0 border border-gray-100"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                      <span className="text-gray-400 text-base font-medium">
                        {(build.projectGroupName || "A").charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="min-w-0">
                    <h1 className="text-[15px] font-medium text-gray-900 leading-snug">
                      {build.projectGroupName || build.name || "Application"}
                    </h1>
                    <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-400">
                      <span>{platformLabel}</span>
                      <span className="text-gray-300">&middot;</span>
                      <span>v{build.version}</span>
                      {build.sizeInBytes ? (
                        <>
                          <span className="text-gray-300">&middot;</span>
                          <span>{formatSize(build.sizeInBytes)}</span>
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>

              {/* Creator description */}
              {build.description && (
                <div className="mx-5 mb-4">
                  <p className="text-sm text-gray-600 italic leading-relaxed break-words whitespace-pre-wrap">
                    {build.description}
                  </p>
                </div>
              )}

              {/* Contextual guidance banner */}
              <div
                className={`mx-5 mb-4 flex items-start gap-2 p-3 rounded-lg border ${guidanceStyle.bg} ${guidanceStyle.border}`}
              >
                <GuidanceIcon icon={guidance.icon} />
                <p className={`text-xs leading-relaxed ${guidanceStyle.text}`}>
                  {t(guidance.messageKey)}
                </p>
              </div>

              {/* Instructions (shown before click) */}
              {guidance.instructionKeys && !hasClicked && (
                <div className="mx-5 mb-4 p-3.5 bg-gray-50 rounded-lg border border-gray-100">
                  <ol className="text-xs text-gray-500 space-y-1.5 list-decimal list-inside">
                    {guidance.instructionKeys.map((key, i) => (
                      <li key={i}>{t(key)}</li>
                    ))}
                  </ol>
                </div>
              )}

              {/* QR code for desktop + mobile builds */}
              {guidance.showQR && (
                <div className="mx-5 mb-4 flex flex-col items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-100">
                  <QRCodeSVG
                    value={pageUrl}
                    size={160}
                    level="M"
                    bgColor="#ffffff"
                    fgColor="#212429"
                  />
                  <p className="text-[11px] text-gray-400 text-center">
                    {t("publicDownload.scanFromDevice")}
                  </p>
                </div>
              )}

              <div className="border-t border-gray-100" />

              {/* CTA */}
              <div className="p-5">
                {isWebGLApp ? (
                  <a

                    href={build.publicUrl!}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full py-3 rounded-lg bg-green-500 hover:bg-green-600 active:bg-green-700 text-white text-center text-sm font-medium transition-colors"
                  >
                    {t(guidance.ctaLabelKey)}
                  </a>
                ) : (
                  <a
                    href={effectiveDownloadUrl}
                    download
                    onClick={() => setHasClicked(true)}
                    className="block w-full py-3 rounded-lg bg-green-500 hover:bg-green-600 active:bg-green-700 text-white text-center text-sm font-medium transition-colors"
                  >
                    {t(guidance.ctaLabelKey)}
                  </a>
                )}

                {/* Post-click confirmation */}
                {hasClicked && (
                  <div className="mt-3 flex items-center gap-2 justify-center">
                    <CheckCircleIcon />
                    <p className="text-xs text-green-600 font-medium">
                      {t("publicDownload.downloadStarted")}
                    </p>
                  </div>
                )}
              </div>

              {/* Expiry */}
              {(build.expiresAt || (build.publicUrl && build.linkType === "app")) && (
                <div className="px-5 pb-4">
                  <p className="text-[11px] text-gray-400 text-center">
                    {build.publicUrl && build.linkType === "app" && !build.expiresAt
                      ? t("publicDownload.permanentLink")
                      : (() => {
                          const expiresAt = build.expiresAt as number;
                          return (
                            <>
                              <span className="font-medium">
                                {formatTimeRemaining(expiresAt, t)}
                              </span>
                              {" \u2014 "}
                              {t("publicDownload.validUntil", {
                                date: new Date(expiresAt).toLocaleDateString(undefined, {
                                  day: "numeric",
                                  month: "long",
                                  year: "numeric",
                                }),
                                time: new Date(expiresAt).toLocaleTimeString(undefined, {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                }),
                              })}
                            </>
                          );
                        })()}
                  </p>
                </div>
              )}
            </div>

            {/* WebUSB direct install section */}
            {showWebUsb && (
              <WebUsbInstallSection build={build} downloadUrl={effectiveDownloadUrl} />
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 py-3 text-center">
        <p className="text-[10px] text-gray-300">{t("publicDownload.poweredBy")}</p>
      </div>
    </div>
  );
};

export default PublicDownloadPage;
